import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, Plus, Trash2, Upload, X, DollarSign, RefreshCw, Loader2 } from "lucide-react";

const emptyItem = () => ({
  card_name: "",
  set_name: "",
  condition: "",
  quantity: 1,
  asking_price: 0,
  notes: "",
  photo_urls: [],
});

const CONDITIONS = ["Mint", "Near Mint", "Lightly Played", "Moderately Played", "Heavily Played", "Damaged", "Sealed", "Graded"];

export default function BuyPage() {
  const { user, isCustomer } = useAuth();
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    preference: "cash",
    items: [emptyItem()],
    message: "",
  });
  const [uploading, setUploading] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) => setForm((f) => {
    const next = f.items.filter((_, i) => i !== idx);
    return { ...f, items: next.length ? next : [emptyItem()] };
  });

  const uploadPhoto = async (idx, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please pick an image file.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("Image is too big — please keep it under 8 MB.");
      return;
    }
    setError("");
    setUploading((u) => ({ ...u, [idx]: (u[idx] || 0) + 1 }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({
        ...f,
        items: f.items.map((it, i) => i === idx ? { ...it, photo_urls: [...it.photo_urls, data.url] } : it),
      }));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed. Please try again.");
    } finally {
      setUploading((u) => ({ ...u, [idx]: Math.max(0, (u[idx] || 1) - 1) }));
    }
  };

  const removePhoto = (idx, photoIdx) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => i === idx ? { ...it, photo_urls: it.photo_urls.filter((_, p) => p !== photoIdx) } : it),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const cleaned = form.items
      .map((it) => ({
        card_name: (it.card_name || "").trim(),
        set_name: (it.set_name || "").trim(),
        condition: (it.condition || "").trim(),
        quantity: Math.max(1, parseInt(it.quantity, 10) || 1),
        asking_price: parseFloat(it.asking_price) || 0,
        notes: (it.notes || "").trim(),
        photo_urls: it.photo_urls,
      }))
      .filter((it) => it.card_name);
    if (cleaned.length === 0) {
      setError("Please add at least one card with a name.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/buy-requests", {
        customer_name: form.customer_name,
        email: form.email,
        phone: form.phone,
        preference: form.preference,
        items: cleaned,
        message: form.message,
      });
      setDone(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="buy-success" className="max-w-2xl mx-auto px-4 sm:px-8 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 text-yellow-400 mx-auto" />
        <h1 className="mt-6 font-[Outfit] text-4xl font-black">Submitted!</h1>
        <p className="mt-4 text-slate-400 leading-relaxed">
          Thanks <span className="text-white font-semibold">{done.customer_name}</span> — we&apos;ll review your cards and
          reach out to <span className="text-white">{done.email}</span> with an offer within 24–48 hours.
        </p>
        <div className="mt-8 bg-slate-900 border border-slate-800 rounded-xl p-6 text-left">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">Submitted items</div>
          <ul className="space-y-2">
            {done.items.map((it, i) => (
              <li key={i} className="flex items-center justify-between text-sm">
                <span className="text-white font-semibold">{it.card_name}</span>
                <span className="text-slate-400 font-mono">× {it.quantity}</span>
              </li>
            ))}
          </ul>
          <div className="text-[10px] text-slate-500 mt-3 pt-3 border-t border-slate-800 font-mono">Ref: {done.id}</div>
        </div>
        <Link to="/" className="mt-6 inline-block text-yellow-400 hover:text-yellow-300 font-mono text-sm uppercase tracking-wider">
          ← Back to home
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="buy-page" className="max-w-3xl mx-auto px-4 sm:px-8 py-16">
      <div className="text-xs tracking-[0.25em] uppercase font-mono text-yellow-400 mb-3">// We buy cards</div>
      <h1 className="font-[Outfit] text-4xl sm:text-5xl font-black tracking-tighter">
        Cash out your <span className="text-yellow-400">collection</span>.
      </h1>
      <p className="mt-3 text-slate-400">
        Tell us what you&apos;ve got, snap a few photos, and we&apos;ll come back with a fair offer.
      </p>

      {/* Rate banner */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-slate-900 border border-yellow-400/40 rounded-xl p-4" data-testid="buy-rate-cash">
          <div className="flex items-center gap-2 text-yellow-400">
            <DollarSign className="w-4 h-4" />
            <span className="text-[10px] font-mono uppercase tracking-[0.2em]">Cash offer</span>
          </div>
          <div className="mt-1 text-2xl font-black text-white font-mono">70–75% <span className="text-slate-400 text-sm font-normal">market</span></div>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-testid="buy-rate-trade">
          <div className="flex items-center gap-2 text-yellow-400">
            <RefreshCw className="w-4 h-4" />
            <span className="text-[10px] font-mono uppercase tracking-[0.2em]">Trade credit</span>
          </div>
          <div className="mt-1 text-2xl font-black text-white font-mono">80% <span className="text-slate-400 text-sm font-normal">market</span></div>
        </div>
      </div>

      {!isCustomer && (
        <div className="mt-6 bg-yellow-400/10 border border-yellow-400/30 rounded-xl p-4 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-yellow-400 mt-0.5 shrink-0" />
          <div className="text-sm text-slate-300">
            Returning seller?{" "}
            <Link to="/signin" className="text-yellow-400 font-bold hover:text-yellow-300">Sign in</Link>{" "}
            to skip typing your info every time.
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-6">
        {/* Contact */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">Your info</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full name" required>
              <input data-testid="buy-name" required value={form.customer_name} onChange={(e) => setField("customer_name", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Email" required>
              <input data-testid="buy-email" type="email" required value={form.email} onChange={(e) => setField("email", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Phone (optional)">
              <input data-testid="buy-phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className={inputCls} />
            </Field>
            <Field label="How do you want paid?">
              <select data-testid="buy-preference" value={form.preference} onChange={(e) => setField("preference", e.target.value)} className={inputCls}>
                <option value="cash">Cash (70–75%)</option>
                <option value="trade_credit">Trade credit (80%)</option>
              </select>
            </Field>
          </div>
        </div>

        {/* Items */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">What are you selling?</h2>
            <span className="text-[10px] font-mono uppercase text-slate-500">{form.items.length} item{form.items.length === 1 ? "" : "s"}</span>
          </div>

          {form.items.map((it, idx) => (
            <div key={idx} data-testid={`buy-item-${idx}`} className="bg-slate-950 border border-slate-800 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase tracking-wider text-yellow-400">Item {idx + 1}</span>
                {form.items.length > 1 && (
                  <button type="button" data-testid={`buy-remove-item-${idx}`} onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-300 text-[10px] font-mono uppercase flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Card name" required>
                  <input data-testid={`buy-cardname-${idx}`} required value={it.card_name} onChange={(e) => updateItem(idx, { card_name: e.target.value })} placeholder="e.g. Charizard ex 199/165" className={inputCls} />
                </Field>
                <Field label="Set (optional)">
                  <input data-testid={`buy-setname-${idx}`} value={it.set_name} onChange={(e) => updateItem(idx, { set_name: e.target.value })} placeholder="e.g. 151, Obsidian Flames" className={inputCls} />
                </Field>
                <Field label="Condition">
                  <select data-testid={`buy-condition-${idx}`} value={it.condition} onChange={(e) => updateItem(idx, { condition: e.target.value })} className={inputCls}>
                    <option value="">— pick condition —</option>
                    {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Quantity">
                  <input data-testid={`buy-qty-${idx}`} type="number" min="1" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Asking price (optional)">
                  <input data-testid={`buy-price-${idx}`} type="number" step="0.01" min="0" value={it.asking_price} onChange={(e) => updateItem(idx, { asking_price: e.target.value })} placeholder="0.00" className={inputCls} />
                </Field>
                <Field label="Notes">
                  <input data-testid={`buy-notes-${idx}`} value={it.notes} onChange={(e) => updateItem(idx, { notes: e.target.value })} placeholder="whitening, back scratch, etc." className={inputCls} />
                </Field>
              </div>

              {/* Photos */}
              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Photos ({it.photo_urls.length}/8)</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {it.photo_urls.map((url, pi) => (
                    <div key={pi} className="relative group aspect-square bg-slate-900 rounded overflow-hidden border border-slate-800">
                      <img src={url} alt={`item ${idx + 1} photo ${pi + 1}`} className="w-full h-full object-cover" />
                      <button type="button" data-testid={`buy-remove-photo-${idx}-${pi}`} onClick={() => removePhoto(idx, pi)} className="absolute top-1 right-1 bg-slate-950/80 text-red-400 hover:text-red-300 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {it.photo_urls.length < 8 && (
                    <label
                      data-testid={`buy-upload-${idx}`}
                      className="aspect-square border-2 border-dashed border-slate-700 hover:border-yellow-400 hover:text-yellow-400 text-slate-400 rounded flex flex-col items-center justify-center cursor-pointer transition-colors"
                    >
                      {uploading[idx] > 0 ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span className="text-[10px] font-mono uppercase mt-1">Uploading</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5" />
                          <span className="text-[10px] font-mono uppercase mt-1">Add photo</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          const remaining = 8 - it.photo_urls.length;
                          files.slice(0, remaining).forEach((f) => uploadPhoto(idx, f));
                          e.target.value = "";
                        }}
                        multiple
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          ))}

          <button type="button" data-testid="buy-add-item" onClick={addItem} className="w-full border border-dashed border-slate-700 hover:border-yellow-400 hover:text-yellow-400 text-slate-400 rounded-lg py-3 text-sm font-mono uppercase tracking-wider transition-colors flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" /> Add another card
          </button>
        </div>

        <Field label="Anything else? (bulk, sealed lots, etc.)">
          <textarea data-testid="buy-message" rows={3} value={form.message} onChange={(e) => setField("message", e.target.value)} className={inputCls} />
        </Field>

        {error && (
          <div data-testid="buy-error" className="text-red-400 bg-red-500/10 border border-red-500/30 px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <button type="submit" disabled={submitting} data-testid="buy-submit" className="w-full bg-yellow-400 text-slate-950 font-bold px-6 py-4 rounded-md uppercase tracking-wide hover:bg-yellow-300 transition-colors disabled:opacity-50">
          {submitting ? "Sending…" : "Send for offer"}
        </button>
        <p className="text-xs text-slate-500 text-center">
          By submitting, you agree we may contact you about your cards. No offer is binding until confirmed in writing.
        </p>
      </form>
    </div>
  );
}

const inputCls = "w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-yellow-400";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}
